// Корень приложения (п.20, финал переезда 2026-09-07): вход, боковое меню,
// выбор проекта, роли и показ текущего экрана. Раньше это была оболочка
// index.html; теперь она — только стили и контейнер.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { alertBox, ask, confirmBox } from './dialog';
import { call, type Data } from './api';
import { filterStore, matchers, type Matchers } from './filter';
import type { ShellCtx } from './shell';
import { Login } from './screens/Login';
import { Menu, TABS, type TabId } from './screens/Menu';
import { WorkCard } from './WorkCard';
import { FloorCard } from './FloorCard';
import { Otmena, OTMENA_OBNOVIT } from './Otmena';
import { Filters } from './screens/Filters';
import { Model } from './screens/Model';
import { RabotyEkran } from './screens/RabotyEkran';
import { Obshchee } from './screens/Obshchee';
import { Spiski } from './screens/Spiski';
import { Struct } from './screens/Struct';
import { Works } from './screens/Works';
import { Podryad } from './screens/Podryad';
import { Shemy } from './screens/Shemy';
import { Links } from './screens/Links';
import { Users } from './screens/Users';
import { Fronts } from './screens/Fronts';
import { Fakt } from './screens/Fakt';
import { FaktLog } from './screens/FaktLog';
import { Kvart } from './screens/Kvart';
import { Resurs } from './screens/Resurs';
import { Rep } from './screens/Rep';
import { Checks } from './screens/Checks';
import { Wiki } from './screens/Wiki';
import { ZamechanieKnopka, ZamechanieProsmotr } from './Zamechanie';

type Me = Data<'/api/auth/me', 'get'>;
type Project = Data<'/api/projects', 'get'>[number];

const SCREEN: Record<TabId, (ctx: ShellCtx) => JSX.Element> = {
  obshchee: c => <Obshchee ctx={c} />,
  // ключи разные: иначе React переиспользует один экран «Модели» между вкладками и стартовый
  // режим (иерархия на «Структуре») не применяется (поймано 2026-10-15)
  plan: c => <Model key="plan" ctx={c} />,
  // «Ручное планирование» — та же таблица и шапка, что «Модель ГПР»,
  // только строки — задачи ручных работ плюс строка быстрого ввода (2026-09-08)
  ruch: c => <Model key="ruch" ctx={c} ruch />,
  // вкладка «Работы» (2026-10-16, шаг 1 по эскизу владельца): свой экран — структура, дни, связи
  // тегами и Гант типового этажа. До того здесь была «Модель» в режиме иерархии
  // (`<Model struktura />`, код цел — Model/Plan/Ierarhiya)
  struktura: c => <RabotyEkran key="struktura" ctx={c} />,
  checks: c => <Checks ctx={c} />, struct: c => <Struct ctx={c} />, works: c => <Works ctx={c} />,
  podryad: c => <Podryad ctx={c} />,
  shemy: c => <Shemy ctx={c} />,
  links: c => <Links ctx={c} />, spiski: c => <Spiski ctx={c} />, resurs: c => <Resurs ctx={c} />,
  kvart: c => <Kvart ctx={c} />, fronty: c => <Fronts ctx={c} />,
  fakt: c => <Fakt ctx={c} />, faktlog: c => <FaktLog ctx={c} />,
  rep: c => <Rep ctx={c} />, users: c => <Users ctx={c} />, wiki: () => <Wiki />,
};
/** на этих экранах панель фильтров не действует */
const NO_FILTERS: TabId[] = ['wiki', 'users', 'obshchee', 'shemy', 'struktura'];   // «Работы» показывают все работы — фильтры не действуют (2026-10-16)
/** «Этажи»: из всей панели нужен только выбор корпусов (владелец 2026-09-09) */
const ONLY_KORPUSA: TabId[] = ['struct'];
/** без кнопок корпусов (владелец 2026-10-05): «Этажи» показывают все корпуса, «Связи» — схема работ */
const BEZ_KORPUSOV: TabId[] = ['struct', 'struktura'];   // «Структура работ» — без выбора корпусов (владелец 2026-10-16)
const DLYA_SVYAZEY: TabId[] = ['links'];
/** «Работы»: без корпусов, этажа и подрядчика; место, этап, группа — кнопками (владелец 2026-09-15) */
const DLYA_RABOT: TabId[] = ['works'];
/** «Модель ГПР»: поле работы — в шапке таблицы, а не в панели (владелец 2026-09-15) */
const BEZ_RABOTY: TabId[] = ['struktura'];   // «Планирование» — поиск работы в панели (2026-10-17), не в шапке таблицы

function readTab(): TabId {
  try {
    const t = localStorage.getItem('plot_tab') as TabId | null;
    return t && TABS.some(x => x.id === t) ? t : 'plan';
  } catch { return 'plan'; }
}
function readPid(): number | null {
  try { return parseInt(localStorage.getItem('plot_pid') || '', 10) || null; } catch { return null; }
}

export function App() {
  const [token, setToken] = useState<string | null>(() => {
    try { return localStorage.getItem('plot_t'); } catch { return null; }
  });
  const [me, setMe] = useState<Me | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [pid, setPid] = useState<number | null>(readPid);
  const [tab, setTab] = useState<TabId>(readTab);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState('');
  const [booted, setBooted] = useState(false);
  const [reload, setReload] = useState(0);
  const [ustar, setUstar] = useState(false);   // даты устарели: сообщение просило «Пересчитать» (2026-10-18)
  const [fm, setFm] = useState<Matchers>(() => matchers());
  const [cardWork, setCardWork] = useState<string | null>(null);   // карточка работы
  const [cardTab, setCardTab] = useState<'fakt' | undefined>(undefined);   // её вкладка
  const [cardFloor, setCardFloor] = useState<{ korpus: string; etazh: string } | null>(null);  // карточка этажа
  const savedTimer = useRef<number | null>(null);

  // вход: кто я и какие проекты доступны
  const boot = useCallback(async () => {
    if (!token) { setBooted(true); return; }
    const m = await call<Me>('get', '/api/auth/me');
    if (!m.ok) {
      try { localStorage.removeItem('plot_t'); } catch { /* приватный режим */ }
      setToken(null); setBooted(true); return;
    }
    setMe(m.data);
    const pr = await call<Project[]>('get', '/api/projects');
    if (pr.ok) {
      setProjects(pr.data);
      setPid(prev => {
        const ok = prev != null && pr.data.some(p => p.id === prev);
        const next = ok ? prev : (pr.data[0]?.id ?? null);
        try { if (next) localStorage.setItem('plot_pid', String(next)); } catch { /* приватный режим */ }
        return next;
      });
    }
    setBooted(true);
  }, [token]);
  useEffect(() => { void boot(); }, [boot]);

  const project = projects.find(p => p.id === pid) || null;
  const role = me
    ? (me.is_admin ? 'администратор' : ((me.roles || {})[String(pid)]?.role || null))
    : null;
  const canPlan = role === 'администратор' || role === 'планер';
  const isGlobal = project?.code === 'global';

  // роль влияет на оформление: скрытые кнопки правки, админские пункты
  useEffect(() => {
    document.body.classList.toggle('noplan', !canPlan);
    document.body.classList.toggle('isadmin', !!me?.is_admin);
  }, [canPlan, me]);

  useEffect(() => { if (pid) filterStore.setProject(pid); }, [pid]);
  useEffect(() => { try { localStorage.setItem('plot_tab', tab); } catch { /* приватный режим */ } }, [tab]);
  // «Типовое планирование» в меню открывает последнюю вкладку из четырёх (владелец 2026-10-16)
  useEffect(() => { if (TIPOVOE.includes(tab)) try { localStorage.setItem('plot_tip_tab', tab); } catch { /* приватный режим */ } }, [tab]);
  const vTipovoe = () => {
    let t: TabId = 'struct';
    try { const s = localStorage.getItem('plot_tip_tab') as TabId | null; if (s && TIPOVOE.includes(s)) t = s; } catch { /* приватный режим */ }
    setTab(t);
  };

  // в «Глобально» доступны только справочники работ, шаблонов связей и списки
  useEffect(() => {
    // «Глобально» (владелец 2026-10-17): вкладка «Работы» — та же структура работ, что в проектах; этажей,
    // планирования и отчётов там нет
    if (isGlobal && !['struktura', 'works', 'links', 'spiski', 'users', 'wiki'].includes(tab)) setTab('struktura');
  }, [isGlobal, tab]);

  function flash(msg?: string) {
    // любое сохранение (экраны, карточки работы и этажа) — стрелки «↺ ↻» перечитывают, что можно отменить
    window.dispatchEvent(new Event(OTMENA_OBNOVIT));
    if (msg && msg.includes('«Пересчитать»')) setUstar(true);
    setSaved(msg || 'Сохранено.');
    setErr('');
    if (savedTimer.current) window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSaved(''), 8000);
  }

  const ctx: ShellCtx = useMemo(() => ({
    pid: pid ?? 0, role, canPlan,
    korpus: fm.korpusValue(), filterKey: fm.filterKey(), filterActive: fm.active(),
    podr: fm.state.podr, reloadToken: reload, datyUstareli: ustar,
    matchesWork: fm.matchesWork, matchesKorpus: fm.matchesKorpus, matchesTask: fm.matchesTask,
    onSaved: flash,
    onError: (m: string) => setErr(m),
    onChanged: () => setReload(x => x + 1),
    onRecalc: () => setReload(x => x + 1),
    onAfterRecalc: () => { setUstar(false); setReload(x => x + 1); },
    onWorkCard: (rabota: string, vkladka?: 'fakt') => { setCardTab(vkladka); setCardWork(rabota); },
    onFloorCard: (korpus: string, etazh: string) => setCardFloor({ korpus, etazh }),
    onGoTab: (t: string, korpus?: string) => {
      if (korpus) filterStore.setKorpus(korpus);
      if (TABS.some(x => x.id === t)) setTab(t as TabId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [pid, role, canPlan, fm, reload, ustar]);

  const stage = typeof window !== 'undefined' && window.location.port === '8081';
  const stageBar = stage ? (
    <div style={{ background: '#b42318', color: '#fff', textAlign: 'center', fontWeight: 700, padding: '4px 8px' }}>
      ТЕСТОВАЯ КОПИЯ (staging, порт 8081) — данные не боевые, правки здесь на прод не попадают
    </div>) : null;

  if (!booted) return <div className="wrap"><div className="muted">Загрузка…</div></div>;
  if (!token || !me) return <>{stageBar}<Login onDone={t => { setToken(t); setBooted(false); }} /></>;

  if (!projects.length) {
    return (
      <div className="wrap">
        <div className="card">
          <b>{me.name || me.email}</b>
          <div className="muted" style={{ marginTop: 6 }}>
            Вам ещё не выдан доступ ни к одному проекту — обратитесь к администратору.
          </div>
          {me.is_admin && <div className="bar"><NewProject onDone={id => { setPid(id); void boot(); }} /></div>}
          <div className="bar"><button className="sec" onClick={logout}>Выйти</button></div>
        </div>
      </div>
    );
  }

  return (
    <>
    {stageBar}
    <div className="layout" id="appView">
      <Menu
        tab={tab} onTab={setTab} me={me} role={role} isGlobal={isGlobal} tipovoe={TIPOVOE} onTipovoe={vTipovoe}
        projects={projects} pid={pid} onProject={id => {
          setPid(id); setUstar(false);
          try { localStorage.setItem('plot_pid', String(id)); } catch { /* приватный режим */ }
          filterStore.set({ korpusa: [] });
          setReload(x => x + 1);
        }}
        // новый проект открывается на «Этажах» — первый шаг планера (владелец 2026-10-15:
        // «сначала этажи, потом работы»); до того оставалась текущая вкладка, чаще пустая «Модель ГПР»
        onNewProject={id => { setPid(id); setTab('struct'); void boot(); }}
        // «Настройки проекта» (2026-10-17): переименование/старт — перечитать список; удалён — boot выберет другой
        onProektIzmenen={m => { flash(m); void boot(); setReload(x => x + 1); }}
        onLogout={logout}
        onLogoutAll={() => void logoutAll()}
      />
      <main className="main">
        {/* четыре вкладки сверху — путь планера (владелец 2026-10-15: «сделай пока сверху 4 вкладки:
            этажи, работы, планирование, отчёты»); остальные экраны пока остаются в левом меню.
            Подсвечивается вкладка, к которой относится открытый экран */}
        {pid && (isGlobal ? tab === 'struktura' : TIPOVOE.includes(tab)) && (
          <nav className="top-vkl" aria-label="Путь планера">
            {/* гнёзда по краям (2026-10-17, эскиз «Планирования»): слева «↺ ↻», справа «Пересчитать» —
                экран кладёт туда свои кнопки; вкладки — посередине */}
            <span className="vkl-l" id="vklL">{canPlan && <Otmena ctx={ctx} />}</span>
            <span className="vkl-c">
              {VKLADKI.filter(v => !isGlobal || v.id === 'struktura').map(v => (
                <a key={v.id} className={v.ekrany.includes(tab) ? 'on' : ''} title={v.title}
                  onClick={() => setTab(v.id)}>{v.label}</a>
              ))}
            </span>
            <span className="vkl-r" id="vklR" />
          </nav>
        )}
        <div className="fwrap" style={{ display: NO_FILTERS.includes(tab) ? 'none' : undefined }}>
          <Filters ctx={{ ...ctx, onFilterChange: setFm }} tolkoKorpusa={ONLY_KORPUSA.includes(tab)}
            dlyaRabot={DLYA_RABOT.includes(tab)} bezRaboty={BEZ_RABOTY.includes(tab)} plan={tab === 'plan' || tab === 'ruch'}
            bezKorpusov={BEZ_KORPUSOV.includes(tab)} dlyaSvyazey={DLYA_SVYAZEY.includes(tab)} />
        </div>
        {/* сообщения «сохранено / ошибка» — всплывают поверх, а не отдельной строкой: пустая
            строка съедала ~25 px над каждым экраном (владелец 2026-10-05, «пустота на Этажах») */}
        <div className="bar msg-plav" style={{ display: err || saved ? undefined : 'none' }}>
          <span className="err">{err}</span>
          <span id="saved" style={{ display: saved ? '' : 'none' }}>{saved}</span>
        </div>
        {pid ? SCREEN[tab](ctx) : <div className="muted">Выберите проект</div>}
      </main>
      {cardWork && pid && (
        <WorkCard key={cardWork + (cardTab || '')} pid={pid} workName={cardWork} can={canPlan}
          vkladka={cardTab}
          onClose={() => setCardWork(null)}
          onChanged={m => { flash(m); setReload(x => x + 1); }} />
      )}
      {cardFloor && pid && (
        <FloorCard pid={pid} korpus={cardFloor.korpus} etazh={cardFloor.etazh} can={canPlan}
          onClose={() => setCardFloor(null)}
          onChanged={m => { flash(m); setReload(x => x + 1); }} />
      )}
    </div>
    {/* замечания «Сообщить о проблеме» (2026-09-12, #20): кнопка на каждом экране
        и просмотр по ссылке из карточки GitHub (/app/#zamechanie=N) */}
    <ZamechanieKnopka pid={pid} sait="app" ekran={TABS.find(x => x.id === tab)?.label} />
    <ZamechanieProsmotr />
    </>
  );

  function logout() {
    try { localStorage.removeItem('plot_t'); } catch { /* приватный режим */ }
    setToken(null); setMe(null); setProjects([]);
  }

  /** погасить прочие свои входы: ссылка на выгрузку несёт токен в адресе,
   *  и если она куда-то попала — доступ отзывается без смены пароля (2026-09-10) */
  async function logoutAll() {
    if (!await confirmBox('Погасить все свои входы, кроме этого? На других устройствах'
      + ' и в старых ссылках доступ перестанет работать.')) return;
    const r = await call<{ pogasheno: number }>('delete', '/api/auth/sessions');
    await alertBox(r.ok ? 'Погашено входов: ' + r.data.pogasheno : r.error.message);
  }
}

/** вкладки сверху (2026-10-15): куда ведёт щелчок и какие экраны к ней относятся (подсветка) */
const VKLADKI: { id: TabId; label: string; title: string; ekrany: TabId[] }[] = [
  { id: 'struct', label: 'Этажи', title: 'Шаг 1: корпуса и этажи проекта', ekrany: ['struct'] },
  // «Работы» → структура работ (владелец 2026-10-15: «иерархия — отдельная сущность», ввод работ только здесь)
  { id: 'struktura', label: 'Работы', title: 'Шаг 2: структура работ — этапы, работы, дни, раскатка, связи',
    ekrany: ['struktura', 'works', 'podryad', 'links', 'spiski', 'resurs', 'kvart'] },
  { id: 'plan', label: 'Планирование', title: 'Шаг 3: модель ГПР — расчёт, Гант, правки сроков',
    ekrany: ['plan', 'ruch', 'checks'] },   // «Ручное» — переключатель в шапке «Планирования» (#125, 2026-10-27)
  // «Отчёты» → экран «Общее» (владелец 2026-10-15): сводка по этапам; прежний скрытый «Отчёты» (rep) — там же по подсветке
  { id: 'obshchee', label: 'Отчёты', title: 'Сводка проекта: старт, этапы — начало и окончание',
    ekrany: ['obshchee', 'rep', 'fronty', 'fakt', 'faktlog'] },
];

/** экраны «Типового планирования»: всё, что подсвечивает одну из четырёх вкладок */
const TIPOVOE: TabId[] = VKLADKI.flatMap(v => [v.id, ...v.ekrany]);

/** создание проекта (администратор) */
function NewProject({ onDone }: { onDone: (id: number) => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <button disabled={busy} onClick={async () => {
      const name = (await ask('Название нового проекта:') || '').trim();
      if (!name) return;
      // библиотека-шаблон (#7): работы, связи и списки из «Глобально»
      const iz = await confirmBox('Взять работы, связи и списки из «Глобально»?',
        { podpis: 'Нет — проект будет пустым, работы можно добавить из «Глобально» позже по одной.', ok: 'Взять', opasno: false });
      setBusy(true);
      const r = await call<{ id: number }>('post', '/api/projects', { name, iz_globalno: iz });
      setBusy(false);
      if (!r.ok) { await alertBox(r.error.message); return; }
      try { localStorage.setItem('plot_pid', String(r.data.id)); } catch { /* приватный режим */ }
      onDone(r.data.id);
    }}>Создать проект</button>
  );
}
